/**
 * Черновики форм: введённое не теряется при перезагрузке.
 *
 * Зачем. Формы витрины заполняются подолгу: в «Проверку текста» несут
 * главу диплома, в «Письмо» — описание ситуации на десять строк. Любая
 * случайность (обновление страницы, промах по «назад», разрядившийся
 * телефон, переход на страницу тарифов за лимитом) стирала всё набранное
 * до последнего знака. Человек при этом не ошибался — он просто закрыл
 * вкладку, и это нормальное действие.
 *
 * Почему localStorage, а не IndexedDB, в которой лежат документы
 * инструментов. Черновик нужен синхронно в первый же кадр отрисовки:
 * асинхронное чтение означало бы, что поле сначала пустое, а через
 * мгновение в нём появляется текст. Это выглядит как чужой ввод и
 * ломает набор, если человек уже начал печатать. Объёмы здесь тоже
 * другие: несколько килобайт текста, а не файлы на сотни.
 *
 * У каждой формы свой ключ (`imya`), и это главное требование: один
 * общий черновик подставлял бы текст письма в поле озвучки.
 */

/** Приставка ко всем ключам: чтобы чужие записи в localStorage не путались с нашими. */
const PRISTAVKA = 'ais-chernovik-';

/**
 * Предел на одну форму, знаков.
 *
 * Не ради экономии места, а ради надёжности: localStorage общий на
 * домен, у него порядка пяти мегабайт на всё, и переполнение
 * выбрасывает исключение при записи. Тогда перестал бы сохраняться не
 * только этот черновик, но и раскладка ленты рядом.
 *
 * Полмиллиона знаков — это около 80 тысяч слов, вдвое больше самого
 * большого предела в наших формах (20 000 знаков у проверки текста).
 */
const PREDEL_ZNAKOV = 500_000;

/**
 * Сколько черновик живёт, мс.
 *
 * Две недели. Меньше нельзя: человек возвращается к диплому через
 * неделю и вправе найти свой текст. Больше не нужно: черновик
 * полугодовой давности человек уже не узнает, а подставленный в форму
 * чужой по ощущению текст хуже пустого поля.
 */
const SROK_MS = 14 * 24 * 60 * 60 * 1000;

interface Zapis {
  /** Значения полей формы: ключ поля — введённая строка. */
  polya: Record<string, string>;
  /** Время последней записи, мс. По нему истекает срок. */
  kogda: number;
}

function dostupno(): boolean {
  // Приватный режим части браузеров отдаёт объект, но бросает при
  // обращении, поэтому проверка именно обращением, а не сравнением с
  // null: иначе падение случилось бы в момент записи, посреди набора
  // текста. Результат обращения не важен, важно что оно не бросило.
  try {
    if (typeof window === 'undefined') return false;
    window.localStorage.getItem(`${PRISTAVKA}proba`);
    return true;
  } catch {
    return false;
  }
}

function klyuch(imya: string): string {
  return `${PRISTAVKA}${imya}`;
}

/**
 * Прочитать черновик формы.
 *
 * Возвращает пустой набор во всех сомнительных случаях: нет
 * хранилища, запись испорчена, срок истёк. Пустое поле — нормальное
 * состояние формы, а исключение здесь остановило бы отрисовку всей
 * страницы.
 */
export function prochitatChernovik(imya: string): Record<string, string> {
  if (!dostupno()) return {};

  try {
    const syroj = window.localStorage.getItem(klyuch(imya));
    if (syroj === null) return {};

    const zapis = JSON.parse(syroj) as Partial<Zapis>;
    if (typeof zapis.kogda !== 'number' || zapis.polya === undefined) return {};

    if (Date.now() - zapis.kogda > SROK_MS) {
      // Просроченное убирается сразу, а не лежит до следующего раза:
      // иначе хранилище копит текст, которым никто не воспользуется.
      window.localStorage.removeItem(klyuch(imya));
      return {};
    }

    // Только строки: в записи мог оказаться объект от старой версии
    // формы, и подстановка его в поле дала бы «[object Object]».
    const chistye: Record<string, string> = {};
    for (const [pole, znachenie] of Object.entries(zapis.polya)) {
      if (typeof znachenie === 'string') chistye[pole] = znachenie;
    }
    return chistye;
  } catch {
    return {};
  }
}

/**
 * Сохранить черновик формы.
 *
 * Пустой набор удаляет запись, а не пишет пустоту: человек, очистивший
 * поле крестиком, не должен находить старый текст после перезагрузки.
 */
export function sohranitChernovik(imya: string, polya: Record<string, string>): void {
  if (!dostupno()) return;

  try {
    // Пустые значения не хранятся: они ничем не отличаются от
    // незаполненного поля, а место занимают.
    const znachimye = Object.fromEntries(
      Object.entries(polya).filter(([, znachenie]) => znachenie !== ''),
    );

    if (Object.keys(znachimye).length === 0) {
      window.localStorage.removeItem(klyuch(imya));
      return;
    }

    const vsego = Object.values(znachimye).reduce((n, s) => n + s.length, 0);
    if (vsego > PREDEL_ZNAKOV) return;

    const zapis: Zapis = { polya: znachimye, kogda: Date.now() };
    window.localStorage.setItem(klyuch(imya), JSON.stringify(zapis));
  } catch {
    // Молча: невозможность сохранить черновик не повод ломать форму.
    // Человек продолжает работать, теряется только страховка.
  }
}

/** Убрать черновик формы. */
export function ubratChernovik(imya: string): void {
  if (!dostupno()) return;
  try {
    window.localStorage.removeItem(klyuch(imya));
  } catch {
    // См. выше.
  }
}
